import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { core } from "../../lib/api";

type CallRecord = { call_id: string; transcript?: string; recording?: { file_key?: string; url?: string } };

export function ContactCallHistory({ contactId }: { contactId: string }) {
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const [loadingRecordings, setLoadingRecordings] = useState<Record<string, boolean>>({});
  const [error, setError] = useState("");
  const history = useQuery({
    queryKey: ["crm-contact-calls", contactId],
    queryFn: () => core<{ calls: CallRecord[] }>(`/api/v1/calls/contacts/${encodeURIComponent(contactId)}`),
    enabled: open,
    // A call that ended seconds ago must show up without a manual Refresh:
    // always refetch when the section is opened, and treat data as stale
    // quickly so focus/refetch picks up newly finished calls.
    refetchOnMount: "always",
    staleTime: 10_000,
  });
  const recording = async (call: CallRecord) => {
    const fileKey = call.recording?.file_key || call.recording?.url;
    if (!fileKey || urls[call.call_id] || loadingRecordings[call.call_id]) return;
    setLoadingRecordings(prev => ({ ...prev, [call.call_id]: true }));
    try {
      const result = await core<{ url: string }>("/api/v1/voice/recording-url", { method: "POST", body: JSON.stringify({ file_key: fileKey }) });
      setUrls(prev => ({ ...prev, [call.call_id]: result.url })); setError("");
    } catch (e) { setError(e instanceof Error ? e.message : "Unable to load recording."); }
    finally { setLoadingRecordings(prev => ({ ...prev, [call.call_id]: false })); }
  };
  return <section className="mt-6 border-t border-line pt-4">
    <div className="flex justify-between"><button onClick={() => setOpen(!open)} className="text-sm font-semibold text-brand">{open ? "Hide call history" : `View call history${history.data ? ` (${history.data.calls.length})` : ""}`}</button>{open && <button onClick={() => void history.refetch()} className="text-xs text-ink-2">Refresh</button>}</div>
    {open && <div className="mt-3 space-y-3">
      <p className="text-xs text-ink-3">Contact ID: {contactId} · Web / Desk calls appear here automatically; SIM calls saved through the Log SIM call form appear here too, with playback when a phone recording was attached.</p>
      {history.isLoading && <p className="text-sm">Loading calls…</p>}
      {(error || history.error) && <p role="alert" className="text-sm text-red-600">{error || (history.error as Error)?.message}</p>}
      {history.data?.calls.length === 0 && <p className="text-sm text-ink-3">No recorded calls yet.</p>}
      {history.data?.calls.map(call => <details
        key={call.call_id}
        className="rounded-lg border border-line p-3"
        onToggle={(event) => {
          if ((event.currentTarget as HTMLDetailsElement).open && call.recording) void recording(call);
        }}
      ><summary className="cursor-pointer text-sm font-semibold">{call.call_id}</summary>{call.recording && loadingRecordings[call.call_id] && <p className="mt-3 text-sm text-ink-3">Loading recording…</p>}{urls[call.call_id] && <audio controls src={urls[call.call_id]} className="mt-2 w-full" />}</details>)}
    </div>}
  </section>;
}
